import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { ArrowRight, Activity, BookOpen, CheckCircle2, Clock } from "lucide-react";
import { Progress } from "@/components/ui/progress";
import { Card, SectionTitle, EmptyState, Tag } from "@/components/lf";
import { useStore, fmtDate, fmtMinutes } from "@/lib/hooks";
import { getBook } from "@/lib/catalog";
import { computeStats, pct } from "@/lib/stats";

const Metric = ({ icon: Icon, label, value, sub, testid }) => (
  <div className="lf-card p-5 lf-card-hover" data-testid={testid}>
    <div className="flex items-center justify-between"><span className="lf-overline">{label}</span><Icon size={16} className="text-[#545B6B]" /></div>
    <div className="font-serif text-5xl mt-4 leading-none" data-testid={`${testid}-value`}>{value}</div>
    <div className="text-xs text-[#929197] mt-2">{sub}</div>
  </div>
);

export default function Ringkasan() {
  const profile = useStore("profile");
  const settings = useStore("settings");
  const progress = useStore("reading_progress");
  const sessions = useStore("reading_sessions");
  const notes = useStore("notes");
  const recs = useStore("recommendations");
  const analyses = useStore("argument_analyses");
  const [q, setQ] = useState("");
  const navigate = useNavigate();
  const st = computeStats(progress, sessions, settings);

  const hour = new Date().getHours();
  const greet = hour < 11 ? "Selamat pagi" : hour < 15 ? "Selamat siang" : hour < 19 ? "Selamat sore" : "Selamat malam";
  const shownRecs = (recs.filter((r) => r.saved).length ? recs.filter((r) => r.saved) : recs).slice(0, 3);
  const goalPct = Math.min(100, Math.round((st.completedThisYear / settings.annualGoalBooks) * 100));
  const firstName = (profile.displayName || "").split(" ")[0];

  const ask = (e) => {
    e.preventDefault();
    if (q.trim()) navigate("/kompas-buku", { state: { question: q.trim() } });
  };

  return (
    <div>
      <div className="pb-8 mb-8 border-b border-[#29292D] lf-fade">
        <div className="lf-overline mb-3">Ringkasan</div>
        <h1 className="text-4xl sm:text-5xl lg:text-6xl font-medium leading-[1.05] tracking-tight" data-testid="dashboard-greeting">{greet}, {firstName}.</h1>
        <p className="text-[#929197] mt-3 max-w-xl">{progress.length === 0 && notes.length === 0 ? "Mulailah dengan satu pertanyaan. Dari sana, bacaan Anda akan menemukan arahnya." : "Berikut kondisi bacaan dan pemikiran Anda saat ini."}</p>
      </div>

      <div className="grid grid-cols-2 xl:grid-cols-4 gap-4 mb-8" data-testid="metric-cards">
        <Metric testid="metric-activity" icon={Activity} label="Aktivitas 7 hari" value={fmtMinutes(st.weekMinutes)} sub={`${st.weekSessions} sesi membaca`} />
        <Metric testid="metric-reading" icon={BookOpen} label="Sedang dibaca" value={st.reading.length} sub="buku" />
        <Metric testid="metric-completed" icon={CheckCircle2} label="Selesai" value={st.completed.length} sub={`${st.completedThisYear} tahun ini`} />
        <Metric testid="metric-time" icon={Clock} label="Total waktu baca" value={fmtMinutes(st.totalMinutes)} sub={`${st.totalPagesRead} halaman dari sesi`} />
      </div>

      <div className="grid grid-cols-1 xl:grid-cols-3 gap-6">
        <div className="xl:col-span-2 space-y-6">
          <Card data-testid="quick-question-card" className="!p-7">
            <div className="lf-overline mb-2">Pertanyaan cepat</div>
            <h2 className="font-serif text-3xl mb-4">Apa yang sedang Anda pikirkan?</h2>
            <form onSubmit={ask} className="flex flex-col sm:flex-row gap-2">
              <input data-testid="quick-question-input" className="lf-field flex-1" value={q} onChange={(e) => setQ(e.target.value)} placeholder="Mis. Bagaimana menghadapi kecemasan akan masa depan?" />
              <button data-testid="quick-question-submit" className="lf-btn">Cari bacaan <ArrowRight size={15} /></button>
            </form>
          </Card>

          <section>
            <SectionTitle action={<Link to="/kompas-buku" className="text-xs text-[#929197] hover:text-[#F2F0EC] underline">Kompas Buku</Link>}>Rekomendasi untuk Anda</SectionTitle>
            {shownRecs.length === 0 ? <EmptyState testid="empty-recommendations" title="Belum ada rekomendasi" text="Ajukan pertanyaan untuk mendapatkan saran buku dari AI."><Link to="/kompas-buku" className="lf-btn">Buka Kompas Buku</Link></EmptyState> : (
              <div className="grid grid-cols-1 md:grid-cols-3 gap-3" data-testid="dashboard-recs">
                {shownRecs.map((r) => (
                  <Link to="/kompas-buku" key={r.id} className="lf-card lf-card-hover p-4 block" data-testid="dashboard-rec-item">
                    <div className="font-serif text-xl leading-tight">{r.book.judul}</div>
                    <div className="text-xs text-[#929197] mt-1">{r.book.penulis}</div>
                    <div className="mt-3">{r.catalogId ? <Tag tone="ivory">Di katalog</Tag> : <Tag tone="warn">Saran AI</Tag>}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>

          <section>
            <SectionTitle action={<Link to="/catatan" className="text-xs text-[#929197] hover:text-[#F2F0EC] underline">Semua catatan</Link>}>Catatan terbaru</SectionTitle>
            {notes.length === 0 ? <EmptyState testid="empty-notes-dash" title="Belum ada catatan" text="Simpan gagasan atau hasil analisis pertama Anda."><Link to="/catatan" className="lf-btn">Tulis catatan</Link></EmptyState> : (
              <div className="border-t border-[#29292D]" data-testid="dashboard-notes">
                {notes.slice(0, 3).map((n) => (
                  <Link to="/catatan" key={n.id} className="block py-4 border-b border-[#29292D] hover:bg-[#101012] px-2 -mx-2" data-testid="dashboard-note-item">
                    <div className="font-serif text-2xl leading-tight">{n.title}</div>
                    <div className="text-xs text-[#929197] mt-1">{fmtDate(n.updatedAt)}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>

        <div className="space-y-6">
          <Card data-testid="dashboard-goal">
            <div className="lf-overline mb-2">Target tahunan {settings.goalYear}</div>
            <div className="font-serif text-5xl">{st.completedThisYear}<span className="text-2xl text-[#929197]"> / {settings.annualGoalBooks}</span></div>
            <Progress value={goalPct} className="h-1.5 bg-[#29292D] mt-3" />
            <div className="text-xs text-[#929197] mt-2">{goalPct}% tercapai · streak {st.streak} hari</div>
          </Card>

          <section>
            <SectionTitle action={<Link to="/progres-membaca" className="text-xs text-[#929197] hover:text-[#F2F0EC] underline">Progres</Link>}>Sedang dibaca</SectionTitle>
            {st.reading.length === 0 ? <EmptyState testid="empty-reading-dash" title="Tidak ada bacaan aktif" text="Tambahkan buku di Progres Membaca."><Link to="/progres-membaca" className="lf-btn">Tambah buku</Link></EmptyState> : (
              <div className="space-y-3" data-testid="dashboard-reading">
                {st.reading.slice(0, 3).map((r) => (
                  <Card key={r.id} className="!p-4" data-testid="dashboard-reading-item">
                    <div className="font-serif text-xl leading-tight">{getBook(r.bookId)?.title}</div>
                    <Progress value={pct(r)} className="h-1.5 bg-[#29292D] mt-3" />
                    <div className="text-xs text-[#929197] mt-1.5">{pct(r)}% · {r.currentPage}{r.totalPages ? `/${r.totalPages}` : ""} hlm</div>
                  </Card>
                ))}
              </div>
            )}
          </section>

          <section>
            <SectionTitle action={<Link to="/bedah-argumen" className="text-xs text-[#929197] hover:text-[#F2F0EC] underline">Bedah Argumen</Link>}>Analisis terbaru</SectionTitle>
            {analyses.length === 0 ? <EmptyState testid="empty-analysis-dash" title="Belum ada analisis" text="Bedah argumen pertama Anda akan muncul di sini." /> : (
              <div className="space-y-2" data-testid="dashboard-analyses">
                {analyses.slice(0, 3).map((a) => (
                  <Link to="/bedah-argumen" key={a.id} className="lf-card lf-card-hover p-4 block" data-testid="dashboard-analysis-item">
                    <div className="text-sm line-clamp-2">{a.result.ringkasan_klaim}</div>
                    <div className="text-xs text-[#929197] mt-1">{fmtDate(a.createdAt)}</div>
                  </Link>
                ))}
              </div>
            )}
          </section>
        </div>
      </div>
    </div>
  );
}
